import React, { useState, useEffect } from "react";
import {
  Activity as ActivityType,
  ActivityData,
  NewActivity
} from "@/entities/activities/types";
import Text from "@/components/ui/text";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import {
  Attachment,
  Calendar,
  Clock,
  Export,
  ExpandView,
  Mail,
  MessageCircle,
  NDA,
  Note,
  People,
  NoteTaker,
  Phone,
  Quote,
  Slack,
  Vector
} from "@dreamhub/designsystem/icons";
import {
  formatToMonthDay,
  getRelativeTime,
  toCalendarDate
} from "@/utils/datetime";
import { cn } from "@/utils/utils";
import HighlightedText from "@/components/controls/HighlightedText";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import ActivityDetails from "../ActivityDetails/ActivityDetails";
import Modal from "@/components/controls/Modal";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import {
  useActivityPeople,
  useActivityTime,
  useActivityTitle,
  useActivityType
} from "@/services/hooks/useActivity";
import IconContainer from "@/components/ui/icon-container";
import Chip from "@/components/ui/chip";
import { useActivityTags } from "@/pages/settings/components/activities/hooks";
import { getTagColorClassesByName } from "@/utils/tags";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { isEmpty, isNull, isUndefined } from "lodash";
import { markdownToHtml } from "@/components/controls/TextEditor/utils/markdownConverter";
import EmailParticipants from "./EmailParticipants";
import ActivityDirectionIndicator from "./ActivityDirectionIndicator";
import { getActivityDirection } from "@/entities/activities/helpers";

interface Props {
  activity: ActivityData;
  isLast: boolean;
  searchQuery: string;
  openEdit: (activityType: ActivityType, activityData: ActivityData) => void;
  openDelete: (activityId: string) => void;
  onDropdownOpenChange: (open: boolean) => void;
  isUpcoming?: boolean;
  isOnlyActivity?: boolean;
  isTasksDrawerOpen: boolean;
  expandedActivityId?: string | null;
  clearExpandedActivityId?: () => void;
  onImportTranscript?: (activity: ActivityData) => void;
  onExportTranscript?: (activity: ActivityData) => void;
  onInviteNotetaker?: (activity: ActivityData) => void;
  onRemoveTag?: (activity: ActivityData, tagId: string) => void;
}

type activityTypeToIconMap = {
  [key in ActivityType]: JSX.Element | string;
};

export const activityTypeToIconMap: activityTypeToIconMap = {
  [ActivityType.CALL]: <Phone />,
  [ActivityType.EMAIL]: <Mail />,
  [ActivityType.TEXT]: <MessageCircle />,
  [ActivityType.IN_PERSON_MEETING]: <Calendar />,
  [ActivityType.ONLINE_MEETING]: <Calendar />,
  [ActivityType.QUOTE_SENT]: <Quote />,
  [ActivityType.NDA_SENT]: <NDA />,
  [ActivityType.NDA_SIGNED]: <NDA />,
  [ActivityType.NOTE]: <Note />,
  [ActivityType.SLACK_MESSAGE]: <Slack />
};

export const activityTypeToNewActivityType: {
  [key in ActivityType]: NewActivity;
} = {
  [ActivityType.CALL]: NewActivity.CALL,
  [ActivityType.EMAIL]: NewActivity.EMAIL,
  [ActivityType.IN_PERSON_MEETING]: NewActivity.MEETING,
  [ActivityType.ONLINE_MEETING]: NewActivity.MEETING,
  [ActivityType.NDA_SENT]: NewActivity.NDA,
  [ActivityType.NDA_SIGNED]: NewActivity.NDA,
  [ActivityType.QUOTE_SENT]: NewActivity.QUOTE,
  [ActivityType.TEXT]: NewActivity.TEXT,
  [ActivityType.NOTE]: NewActivity.NOTE,
  [ActivityType.SLACK_MESSAGE]: NewActivity.SLACK_MESSAGE
};

export const DIVIDER_CLASSNAME =
  "flex items-center gap-1 w-fit [&:not(:last-child)]:after:content-[''] [&:not(:last-child)]:after:h-4 [&:not(:last-child)]:after:w-[1px] [&:not(:last-child)]:after:bg-border-border2 [&:not(:last-child)]:after:mx-1";

const Activity: React.FC<Props> = ({
  activity,
  isLast,
  searchQuery,
  openEdit,
  openDelete,
  onDropdownOpenChange,
  isUpcoming,
  isOnlyActivity,
  isTasksDrawerOpen,
  expandedActivityId,
  clearExpandedActivityId,
  onImportTranscript,
  onExportTranscript,
  onInviteNotetaker,
  onRemoveTag
}) => {
  const { t } = useTranslation();
  const { type, notes, peopleIds } = activity;
  const isCreatedAtActivity = isUndefined(type);
  const isNoteActivity = type === ActivityType.NOTE;
  const isMeetingActivity =
    type === ActivityType.IN_PERSON_MEETING ||
    type === ActivityType.ONLINE_MEETING;
  const isSlackActivity = type === ActivityType.SLACK_MESSAGE;
  const isCallActivity = type === ActivityType.CALL;
  const direction = getActivityDirection(activity);

  // Notetaker invites only make sense for meetings that haven't happened yet
  const onInviteNotetakerForActivity =
    isMeetingActivity && isUpcoming && !notes?.isCancelled
      ? onInviteNotetaker
      : undefined;

  const [isExpandedView, setIsExpandedView] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setIsExpandedView(true);
    }
  };

  const people = useActivityPeople(peopleIds, notes);
  const detailsPeople = useActivityPeople(peopleIds, notes, true);

  const time = useActivityTime(notes);

  const title = useActivityTitle(activity);
  const activityType = useActivityType(t, activity);

  const handleExpandFromDropdown = () => {
    setIsExpandedView(true);
  };

  useEffect(() => {
    if (expandedActivityId === activity.id && !isExpandedView) {
      setIsExpandedView(true);
    }
  }, [expandedActivityId, activity.id, isExpandedView]);

  const handleCloseExpanded = () => {
    setIsExpandedView(false);
    if (clearExpandedActivityId) {
      clearExpandedActivityId();
    }
  };

  const renderAttachments = () => (
    <div className="flex items-center gap-[6px] px-2 py-[6px]">
      <IconContainer size="sm" color="tertiary">
        <Attachment />
      </IconContainer>
      <Text variant="labelSecondary">{notes?.attachments?.length}</Text>
    </div>
  );

  const activityTags = useActivityTags(
    type || ActivityType.IN_PERSON_MEETING,
    Boolean(type)
  );

  const renderTags = () => {
    if (!activity.tags || activity.tags.length === 0) return null;

    const tagsData = activityTags.data || [];

    return (
      <div className="flex gap-1 flex-wrap">
        {activity.tags.map((tagId) => {
          const tagData = tagsData.find((tag) => tag.id.toString() === tagId);
          const tagName = tagData?.name || tagId;

          return (
            <Chip
              key={tagId}
              label={tagName}
              filled
              removable={Boolean(onRemoveTag)}
              removeAriaLabel={t(
                "activities.activityModal.removeTagAriaLabel",
                { tagName }
              )}
              onRemove={
                onRemoveTag
                  ? () => onRemoveTag(activity, tagId)
                  : undefined
              }
              className={cn(
                "py-1 px-2 rounded-md",
                getTagColorClassesByName(tagName)
              )}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full flex gap-2 items-start justify-between">
      <div className="flex min-h-[3rem] gap-10 w-full relative">
        {!isOnlyActivity && !isLast && (
          <div
            className={cn(
              "absolute left-[19px] top-[40px] h-[calc(100%-6px)] bg-border-border2 w-[1.5px]",
              isUpcoming &&
                "[mask-image:repeating-linear-gradient(0deg,black_0px,black_4px,transparent_4px,transparent_8px)]"
            )}
          />
        )}
        <div className="flex gap-4 w-[7.5rem] h-fit">
          <LogoIconContainer
            index={-1}
            bgColor="var(--grey-800)"
            iconSize="5"
            icon={
              isCreatedAtActivity ? (
                <Vector />
              ) : (
                activityTypeToIconMap[type as ActivityType]
              )
            }
          />
          {notes?.date && (
            <Text variant="bodySecondary" className="self-center">
              <HighlightedText
                text={formatToMonthDay(notes?.date)}
                query={searchQuery}
                className="whitespace-pre-wrap"
              />
            </Text>
          )}
        </div>
        <Popover open={false} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <div
              className={cn(
                "group flex flex-col w-full rounded-2xl p-3 bg-surface-widget-card transition-all duration-200 ease-in-out border border-border-border1",
                !isCreatedAtActivity &&
                  "cursor-pointer hover:bg-surface-widget-card-hover"
              )}
            >
              <div className="flex w-full pl-2 items-center justify-between">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Text
                      variant="body"
                      className={cn(notes?.isCancelled && "line-through")}
                    >
                      <HighlightedText
                        text={isNoteActivity ? t("activities.note") : title}
                        query={searchQuery}
                        className="whitespace-pre-wrap"
                      />
                    </Text>
                    {!isCreatedAtActivity && !isNoteActivity && (
                      <Text variant="bodyTertiary">
                        <HighlightedText
                          text={`(${activityType})`}
                          query={searchQuery}
                          className="whitespace-pre-wrap"
                        />
                      </Text>
                    )}
                    {direction && (
                      <ActivityDirectionIndicator direction={direction} />
                    )}
                    {notes?.isCancelled && (
                      <Text variant="lgBody" className="px-1 text-error">
                        {t("activities.canceled")}
                      </Text>
                    )}
                  </div>
                  {!notes?.isCancelled && isUpcoming && notes?.date && (
                    <Text variant="lgBody" className="text-natural">
                      {getRelativeTime(toCalendarDate(notes.date))}
                    </Text>
                  )}
                  {renderTags()}
                </div>
                {!isCreatedAtActivity && (
                  <div className="flex items-center gap-2 ">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpandFromDropdown();
                      }}
                      className="opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100"
                      variant="icon"
                      size="lg"
                    >
                      <ExpandView />
                    </IconButton>
                    <OptionsDropdownMenu
                      className={cn(
                        "opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100",
                        isDropdownOpen && "opacity-100"
                      )}
                      options={
                        {
                          ...(!isSlackActivity && {
                            edit: () =>
                              openEdit(activity.type as ActivityType, activity)
                          }),
                          ...(onInviteNotetakerForActivity && {
                            inviteNotetaker: {
                              action: () =>
                                onInviteNotetakerForActivity(activity),
                              customIcon: (
                                <IconContainer size="5" color="secondary">
                                  <NoteTaker />
                                </IconContainer>
                              )
                            }
                          }),
                          ...((isMeetingActivity || isCallActivity) &&
                            notes?.hasTranscript &&
                            onExportTranscript && {
                              exportTranscript: {
                                action: () => onExportTranscript(activity),
                                customIcon: (
                                  <IconContainer size="5" color="secondary">
                                    <Export />
                                  </IconContainer>
                                )
                              }
                            }),
                          delete: {
                            action: () => openDelete(activity.id),
                            variant: "destructive"
                          }
                        } as DetailedViewOptions
                      }
                      tPrefix="activities.options"
                      buttonVariant="icon"
                      iconSize="lg"
                      iconColor="primary"
                      onOpenChange={(open) => {
                        setIsDropdownOpen(open);
                        if (!open) {
                          onDropdownOpenChange(false);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              {(peopleIds ||
                (notes?.startTime && notes?.endTime) ||
                notes?.date ||
                (type === ActivityType.EMAIL &&
                  (notes?.fromEmail || !isEmpty(notes?.toEmails)))) &&
                !isNoteActivity && (
                  <div className="flex items-center gap-2 pt-2">
                    {type === ActivityType.EMAIL ? (
                      <EmailParticipants notes={notes} searchQuery={searchQuery} />
                    ) : !isEmpty(peopleIds) ? (
                      <div className="flex items-center gap-[6px] px-2 py-[6px]">
                        <IconContainer
                          className="!size-[18px]"
                          color="secondary"
                        >
                          <People />
                        </IconContainer>
                        <Text variant="bodySecondary">
                          <HighlightedText
                            text={people}
                            query={searchQuery}
                            className="whitespace-pre-wrap"
                          />
                        </Text>
                      </div>
                    ) : null}
                    {!isNull(time) && (
                      <div className="flex items-center gap-[6px] px-2 py-[6px]">
                        <IconContainer
                          className="!size-[18px]"
                          color="secondary"
                        >
                          <Clock />
                        </IconContainer>
                        <Text variant="bodySecondary">
                          <HighlightedText
                            text={time}
                            query={searchQuery}
                            className="whitespace-pre-wrap"
                          />
                        </Text>
                      </div>
                    )}
                    {type !== ActivityType.EMAIL &&
                      !isEmpty(notes?.attachments) &&
                      renderAttachments()}
                  </div>
                )}
              {type === ActivityType.EMAIL && (
                <div className="flex gap-1 pl-2">
                  {!isEmpty(notes?.attachments) && renderAttachments()}
                  {notes?.emailContent && (
                    <div className="flex items-center gap-[6px]">
                      <Text variant="bodySecondary" className="line-clamp-2">
                        <span
                          className="[&_h1]:text-xl [&_h1]:font-semibold [&_h1]:capitalize [&_h1]:text-primary [&_h1]:my-4 [&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:text-primary [&_h2]:my-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:leading-[22px] [&_h3]:text-primary [&_h3]:my-2 [&_p]:text-base [&_p]:font-normal [&_p]:leading-5 [&_p]:text-primary [&_p]:my-2 [&_ul]:text-primary [&_ol]:text-primary [&_li]:text-primary [&_ul>li]:list-disc [&_ol>li]:list-decimal [&_ul]:ml-6 [&_ol]:ml-6 [&_li]:ml-0 [&_a]:text-link [&_a]:underline [&_a]:cursor-pointer hover:[&_a]:text-link-hover"
                          dangerouslySetInnerHTML={{
                            __html: markdownToHtml(notes?.emailContent || "")
                          }}
                        />
                      </Text>
                    </div>
                  )}
                </div>
              )}
              {notes?.summary?.summary && (
                <div className="flex p-2 gap-1 flex-col">
                  {!isNoteActivity && (
                    <Text variant="body">{t("activities.summary")}</Text>
                  )}
                  <div className="flex flex-col gap-1">
                    <div className="overflow-hidden transition-[max-height] duration-300 ease-in-out">
                      <Text
                        variant="bodySecondary"
                        className="w-full transition-all duration-75 line-clamp-2"
                      >
                        {isNoteActivity ||
                        isMeetingActivity ||
                        type === ActivityType.SLACK_MESSAGE ||
                        type === ActivityType.CALL ? (
                          <span
                            className="[&_h1]:text-xl [&_h1]:font-semibold [&_h1]:capitalize [&_h1]:text-primary [&_h1]:my-4 [&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:text-primary [&_h2]:my-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:leading-[22px] [&_h3]:text-primary [&_h3]:my-2 [&_p]:text-base [&_p]:font-normal [&_p]:leading-5 [&_p]:text-primary [&_p]:my-2 [&_ul]:text-primary [&_ol]:text-primary [&_li]:text-primary [&_ul>li]:list-disc [&_ol>li]:list-decimal [&_ul]:ml-6 [&_ol]:ml-6 [&_li]:ml-0 [&_a]:text-link [&_a]:underline [&_a]:cursor-pointer hover:[&_a]:text-link-hover"
                            dangerouslySetInnerHTML={{
                              __html: markdownToHtml(
                                notes?.summary?.summary || ""
                              )
                            }}
                          />
                        ) : (
                          notes?.summary?.summary
                        )}
                      </Text>
                    </div>
                    {notes?.summary?.summary &&
                      notes.summary.summary.length > 100 && (
                        <Button
                          variant="tertiary"
                          className="self-start"
                        >
                          {t("readMore")}
                        </Button>
                      )}
                  </div>
                </div>
              )}
              {notes?.messageContent && (
                <div className="flex pl-2">
                  <Text
                    variant="bodySecondary"
                    className={cn(
                      "truncate w-full",
                      isTasksDrawerOpen ? "max-w-[30rem]" : "max-w-3xl"
                    )}
                  >
                    {notes?.messageContent}
                  </Text>
                </div>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            padding={0}
            className="max-h-[25rem] min-w-[30rem] w-fit max-w-[45rem]"
            align="start"
            side="top"
            sideOffset={4}
          >
            {/* Popover content is no longer used - activities open directly in expanded view */}
            <div />
          </PopoverContent>
        </Popover>
        <Modal
          open={isExpandedView && !isCreatedAtActivity}
          setIsOpen={(open) => {
            if (!open) {
              handleCloseExpanded();
            }
          }}
          showCloseButton={false}
          hideDivider
          headerClassname="hidden"
          contentClassname="w-[64rem] h-fit"
          content={
            <ActivityDetails
              activity={activity}
              title={title}
              icon={activityTypeToIconMap[type as ActivityType]}
              activityType={activityType}
              closePopover={handleCloseExpanded}
              people={detailsPeople}
              time={time}
              openEdit={openEdit}
              openDelete={openDelete}
              isExpandedView={isExpandedView}
              onImportTranscript={onImportTranscript}
              onInviteNotetaker={onInviteNotetakerForActivity}
            />
          }
        />
      </div>
    </div>
  );
};

export default Activity;
